import { typographyPlugin } from './utils/tailwind/plugins/typography'
import { btShadowsPlugin } from './utils/tailwind/plugins/boxShadow'

import type { Config } from 'tailwindcss'

const customDimensions = {
  13: '3.25rem',
  15: '3.75rem',
  18: '4.5rem',
  19: '4.75rem',
  21: '5.25rem',
  22: '5.5rem',
  23: '5.75rem',
  24: '6rem',
  26: '6.5rem',
  27: '6.75rem',
  30: '7.5rem',
  31: '7.75rem',
  34: '8.5rem',
  35: '8.75rem',
  38: '9.5rem',
  39: '9.75rem',
  40: '10rem',
  42: '10.5rem',
  46: '11.5rem',
  49: '12.25rem',
  50: '12.5rem',
  52: '13rem',
  53: '13.25rem',
  54: '13.5rem',
  56: '14rem',
  58: '14.5rem',
  60: '15rem',
  62: '15.5rem',
  64: '16rem',
  66: '16.5rem',
  68: '17rem',
  70: '17.5rem',
  73: '18.25rem',
  74: '18.5rem',
  75: '18.75rem',
  79: '19.75rem',
  81: '20.25rem',
  82: '20.5rem',
  83: '20.75rem',
  84: '21rem',
  85: '21.25rem',
  90: '22.5rem',
  100: '25rem',
  104: '26rem',
  106: '26.5rem',
  108: '27rem',
  109: '27.25rem',
  112: '28rem',
  114: '28.5rem',
  115: '28.75rem',
  116: '29rem',
  120: '30rem',
  125: '31.25rem',
  127: '31.75rem',
  128: '32rem',
  130: '32.5rem',
  131: '32.75rem',
  132: '33rem',
  134: '33.5rem',
  135: '33.75rem',
  136: '34rem',
  138: '34.5rem',
  140: '35rem',
  144: '36rem',
  145: '36.25rem',
  147: '36.75rem',
  150: '37.5rem',
  151: '37.75rem',
  154: '38.5rem',
  155: '38.75rem',
  157: '39.25rem',
  158: '39.5rem',
  159: '39.75rem',
  160: '40rem',
  162: '40.5rem',
  164: '41rem',
  166: '41.5rem',
  170: '42.5rem',
  174: '43.5rem',
  176: '44rem',
  186: '46.5rem',
  190: '47.5rem',
  200: '50rem',
  204: '51rem',
  210: '52.5rem',
  244: '61rem',
  270: '67.5rem',
  282: '70.5rem',
  300: '75rem',
  319: '79.75rem',
  330: '82.5rem',
  354: '88.5rem',
  400: '100rem',
  460: '115rem'
}

export const SCREEN_XS = 320
const customSpacings = {
  5: '1.25rem',
  18: '4.5rem',
  22: '5.5rem',
  354: '88.5rem'
}

export const SCREEN_SM = 768
export const SCREEN_MD = 1024
export const SCREEN_LG = 1200
export const SCREEN_XL = 1440

const SCREENS = {
  sm: `${SCREEN_SM}px`,
  md: `${SCREEN_MD}px`,
  lg: `${SCREEN_LG}px`,
  xl: `${SCREEN_XL}px`
}

export default <Config>{
  content: [
    './components/**/*.{vue,js,ts}',
    './layouts/**/*.vue',
    './pages/**/*.vue',
    './composables/**/*.{vue,js,ts}',
    './widgets/**/*.vue',
    './storyblok/**/*.{vue,js,ts}',
    './emails/**/*.{vue,js,ts}',
    './app.vue'
  ],
  plugins: [typographyPlugin, btShadowsPlugin],
  theme: {
    container: {
      center: true,
      padding: {
        DEFAULT: '1.25rem',
        sm: '1.25rem',
        md: '1.25rem',
        lg: '0',
        xl: '0'
      },
      screens: {
        sm: '100%',
        md: '100%',
        lg: '1128px',
        xl: '1416px'
      }
    },
    screens: SCREENS,
    fontFamily: {
      sans: ['Roboto', 'sans-serif']
    },
    fontSize: {
      '2xs': '0.5rem',
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      md: '1.125rem',
      lg: '1.25rem',
      xl: '1.5rem',
      '2xl': '1.75rem',
      '3xl': '2rem',
      '4xl': '2.5rem',
      '5xl': '3rem',
      '6xl': '3.5rem',
      '7xl': '4rem',
      '8xl': '4.5rem'
    },
    boxShadow: {
      none: '0 0 #0000',
      xs: '0 1px 4px 0 rgba(192,200,216,.3)',
      sm: '0 4px 8px 0 rgba(21, 38, 73, 0.20)',
      md: '0 8px 32px 0 rgba(21, 38, 73, 0.16)',
      lg: '0 8px 48px 0 rgba(21, 38, 73, 0.08)',
      card: '0px 8px 32px 0px rgba(21, 38, 73, 0.16)',
      'top-md': '0 -4px 16px 0 rgba(21, 38, 73, 0.16)',
      sidebar: '0 0 8px 0 rgba(21, 38, 73, 0.20)',
      green: '4px 0 0 0 rgba(205, 218, 50, 1) inset',
      modal: '0 0 8px 0 rgba(21,38,73,0.33)',
      slider: '0 4px 32px 0 rgba(21, 38, 73, 0.08)',
      'slider-lg': '0 4px 32px 0 rgba(21, 38, 73, 0.12)',
      counter: '0px 4px 40px 0px rgba(21, 38, 73, 0.14)',
      'counter-shadow': '0px 4px 40px 0px rgba(21, 38, 73, 0.14)',
      'bbb-slider': '0px 16px 32px 0px rgba(21, 38, 73, 0.14)'
    },
    borderRadius: {
      DEFAULT: '0.25rem',
      none: '0',
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      '2md': '2rem',
      lg: '2.5rem',
      full: '9999px',
      bubble: '50% 50% 50% 12%',
      'bubble-top-right': '50% 12% 50% 50%'
    },
    extend: {
      colors: {
        secondary: '#4C5367',
        'secondary-light': '#6F7495',
        bt: {
          green: {
            DEFAULT: '#CDDA32',
            active: '#ACB90C',
            secondary: '#BCC827',
            hover: '#D9EC00',
            'hover-muted': '#BBC72C',
            light: '#CDDA320D',
            dull: '#F4F8DC',
            pale: '#EAEFB0'
          },
          'light-gray': '#FAFAF8',
          blue: {
            dark: '#152649',
            ui: '#223968',
            'ui-light': '#204588',
            info: '#F1F6FB',
            soft: '#CFDCF5',
            pale: '#ECF3FF'
          },
          red: {
            DEFAULT: '#CD5A5B',
            dark: '#9d0025'
          },
          grabout: {
            violet: {
              DEFAULT: '#8121BA'
            },
            red: {
              DEFAULT: '#EB0000'
            }
          }
        },
        link: '#313892',
        divider: '#DADEE7',
        border: '#C0C8D8',
        grey: {
          DEFAULT: '#F7F7F7',
          light: '#F8F9FF',
          select: '#E8EBF0',
          subtitle: '#737D92',
          secondary: '#69768F',
          dark: '#4C5367'
        },
        light: {
          error: '#FBF1F1',
          warning: '#FFF5E4',
          success: '#F4F7D4',
          info: '#EFF8FF'
        },
        dark: {
          error: '#BC3C3C',
          warning: '#EF8D34',
          success: '#CDDA32',
          info: '#204588'
        },
        focus: '#009EF0',
        dullIcon: '#999',
        input: {
          grey: '#EBEEF5'
        },
        calendar: {
          day: {
            selected: '#f0f4fb',
            outline: '#e2e3e8'
          }
        },
        captcha: {
          icon: '#ffa000',
          'top-bg': '#fff5e4',
          'bottom-bg': '#f8ecd7'
        }
      },
      lineHeight: {
        11: '2.75rem',
        14: '3.5rem',
        15: '3.75rem',
        16: '4rem',
        17: '4.5rem',
        18: '5rem',
        19: '5.5rem'
      },
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' }
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' }
        },
        'slide-up': {
          from: { height: 'var(--radix-collapsible-content-height)' },
          to: { height: '0' }
        },
        'slide-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-collapsible-content-height)' }
        },
        'menu-overlay-in': {
          from: { opacity: '0' },
          to: { opacity: '1' }
        },
        'menu-overlay-out': {
          from: { opacity: '1' },
          to: { opacity: '0' }
        },
        'menu-drop-in': {
          from: { transform: 'translate3d(0,-100%,0)' },
          to: { transform: 'translate3d(0,0,0)' }
        },
        'menu-drop-out': {
          from: { transform: 'translate3d(0,0,0)' },
          to: { transform: 'translate3d(0,-100%,0)' }
        },
        'icon-bounce': {
          '0%, 65%': { transform: 'scale(1)' },
          '68%': { transform: 'scale(1.2)' },
          '100%': { transform: 'scale(1)' }
        },
        'marquee-content': {
          from: { transform: 'translateX(0%)' },
          to: { transform: 'translateX(-100%)' }
        },
        'marquee-track': {
          from: { transform: 'translateX(0%)' },
          to: { transform: 'translateX(-50%)' }
        }
      },
      animation: {
        'accordion-down': 'accordion-down 0.3s ease-out',
        'accordion-up': 'accordion-up 0.3s ease-out',
        'slide-down': 'slide-down 0.3s ease-out',
        'slide-up': 'slide-up 0.3s ease-out',
        spin: 'spin 1.5s linear infinite',
        'menu-overlay-in': 'menu-overlay-in 160ms ease-out forwards',
        'menu-overlay-out': 'menu-overlay-out 150ms ease-in forwards',
        'menu-drop-in': 'menu-drop-in 260ms cubic-bezier(0.2,0.8,0.2,1) forwards',
        'menu-drop-out': 'menu-drop-out 200ms ease-in forwards',
        'icon-bounce': 'icon-bounce 2.6s infinite',
        'marquee-item': 'marquee-content 20s linear infinite',
        'marquee-track': 'marquee-track 20s linear infinite'
      },
      padding: {
        ...customDimensions
      },
      width: {
        ...customDimensions
      },
      maxWidth: {
        ...customDimensions
      },
      minWidth: {
        ...customDimensions
      },
      height: {
        ...customDimensions
      },
      maxHeight: {
        ...customDimensions
      },
      minHeight: {
        ...customDimensions
      },
      size: {
        ...customDimensions
      },
      gap: {
        ...customDimensions
      },
      margin: {
        ...customSpacings
      },
      zIndex: {
        '1': '1',
        '2': '2',
        '5': '5',
        '999': '999'
      },
      gridAutoRows: {
        '1': '1fr'
      },
      spacing: {
        19: '4.75rem',
        22: '5.5rem',
        'screen-1/2': '50vw'
      },
      borderWidth: {
        ...customSpacings
      },
      aspectRatio: {
        landscape: '4/3'
      }
    }
  },
  safelist: [
    'bg-bt-green',
    'bg-bt-blue-ui',
    'bg-bt-grabout-violet',
    'bg-bt-grabout-red',
    'shadow-bt-green',
    'shadow-bt-blue-ui',
    'shadow-bt-grabout-violet',
    'shadow-bt-grabout-red',
    { pattern: /grid-cols-(\d{1,2})/ }
  ]
}
